import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { LogOut, Loader2, Wrench, Mail, Phone, FileText, ArrowLeft, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { CONTACT } from "@/lib/site";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  recue: { label: "Reçue", cls: "border-line bg-night text-slate-400" },
  en_cours: { label: "En cours", cls: "border-neon/40 bg-neon/10 text-neon" },
  termine: { label: "Terminé", cls: "border-green-400/40 bg-green-400/10 text-green-400" },
};

const STATUS_OPTIONS = [
  ["recue", "Reçue"],
  ["en_cours", "En cours"],
  ["termine", "Terminé"],
];

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

function GoogleG({ size = 17 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.3-2.1 3.7-5.2 3.7-8.6z" />
      <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.2 0-5.9-2.1-6.8-5.1L1.3 17.2C3.3 21.2 7.3 24 12 24z" />
      <path fill="#FBBC05" d="M5.2 14.3c-.2-.7-.4-1.5-.4-2.3s.1-1.6.4-2.3L1.3 6.8C.5 8.4 0 10.1 0 12s.5 3.6 1.3 5.2l3.9-2.9z" />
      <path fill="#EA4335" d="M12 4.7c2.3 0 3.8.9 4.7 1.8l3.4-3.3C18 1.2 15.2 0 12 0 7.3 0 3.3 2.8 1.3 6.8l3.9 2.9c.9-2.9 3.6-5 6.8-5z" />
    </svg>
  );
}

function LoginCard({ login }) {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const { loginWithPassword, register, forgotPassword } = useAuth();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      if (mode === "login") {
        await loginWithPassword(form.email, form.password);
        toast.success("Bienvenue !");
      } else if (mode === "register") {
        await register(form.name, form.email, form.password);
        toast.success("Compte créé — bienvenue chez MAXREVIX !");
      } else {
        await forgotPassword(form.email);
        toast.success("Si un compte existe avec cet email, un lien de réinitialisation vient de partir (valable 1 h).");
        setMode("login");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail), { duration: 6000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-10 rounded-3xl border border-neon/25 bg-gradient-to-b from-neon/10 via-ink to-ink p-8 text-center lg:p-12">
      <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl border border-neon/25 bg-neon/10 text-neon">
        <Wrench size={28} />
      </span>
      <h1 data-testid="profile-login-title" className="mt-6 font-display text-4xl font-extrabold uppercase tracking-tight sm:text-5xl">
        Espace client
      </h1>
      <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-slate-400">
        {mode === "register"
          ? "Créez votre compte pour suivre vos demandes de devis."
          : mode === "forgot"
            ? "Entrez votre email : nous vous envoyons un lien de réinitialisation valable 1 h."
            : "Connectez-vous pour suivre vos demandes de devis et retrouver votre historique."}
      </p>

      <form onSubmit={submit} data-testid={`auth-form-${mode}`} className="mx-auto mt-8 grid max-w-sm gap-4 text-left">
        {mode === "register" && (
          <label className="grid gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neon label-mono">Nom & prénom</span>
            <input data-testid="auth-input-name" required minLength={2} maxLength={120} autoComplete="name" className="field-input" placeholder="Camille Dupont" value={form.name} onChange={set("name")} />
          </label>
        )}
        <label className="grid gap-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neon label-mono">Email</span>
          <input data-testid="auth-input-email" required type="email" autoComplete="email" className="field-input" placeholder="vous@exemple.fr" value={form.email} onChange={set("email")} />
        </label>
        {mode !== "forgot" && (
          <label className="grid gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neon label-mono">Mot de passe</span>
            <input data-testid="auth-input-password" required minLength={mode === "register" ? 8 : 1} type="password" autoComplete={mode === "register" ? "new-password" : "current-password"} className="field-input" placeholder={mode === "register" ? "8 caractères minimum" : "Votre mot de passe"} value={form.password} onChange={set("password")} />
          </label>
        )}

        <button
          data-testid={`auth-submit-${mode}`}
          type="submit"
          disabled={busy}
          className="flex items-center justify-center gap-2 rounded-full bg-neon px-6 py-3.5 text-xs font-extrabold uppercase tracking-wider text-night chip-glow transition-transform duration-200 hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {busy ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-night/30 border-t-night" /> : null}
          {mode === "login" ? "Se connecter" : mode === "register" ? "Créer mon compte" : "Recevoir le lien"}
        </button>
      </form>

      <div className="mx-auto mt-6 flex max-w-sm flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs">
        {mode !== "register" && (
          <button data-testid="auth-switch-register" onClick={() => setMode("register")} className="font-semibold text-neon hover:underline">
            Créer un compte
          </button>
        )}
        {mode !== "login" && (
          <button data-testid="auth-switch-login" onClick={() => setMode("login")} className="font-semibold text-neon hover:underline">
            Se connecter
          </button>
        )}
        {mode === "login" && (
          <button data-testid="auth-switch-forgot" onClick={() => setMode("forgot")} className="font-semibold text-slate-400 hover:text-neon">
            Mot de passe oublié ?
          </button>
        )}
      </div>

      <div className="mx-auto mt-8 flex max-w-sm items-center gap-4">
        <span className="h-px flex-1 bg-line" />
        <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">ou</span>
        <span className="h-px flex-1 bg-line" />
      </div>

      <button
        data-testid="profile-google-login-btn"
        onClick={login}
        className="mx-auto mt-6 flex items-center gap-3 rounded-full bg-[#ffffff] px-7 py-4 text-sm font-extrabold text-[#1f1f1f] transition-transform duration-200 hover:scale-[1.03]"
      >
        <GoogleG />
        Continuer avec Google
      </button>
    </div>
  );
}

export default function Profile() {
  useEffect(() => {
    document.title = "Espace client — MAXREVIX";
  }, []);

  const location = useLocation();
  const { user: authUser, checking, login, logout } = useAuth();
  const user = location.state?.user || authUser;
  const [quotes, setQuotes] = useState(null);
  const [adminQuotes, setAdminQuotes] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    axios
      .get(`${API}/admin/analytics`, { withCredentials: true })
      .then((r) => setStats(r.data))
      .catch(() => setStats(false));
  }, [isAdmin]);

  useEffect(() => {
    if (!user) return;
    axios
      .get(`${API}/quotes/mine`, { withCredentials: true })
      .then((r) => setQuotes(r.data))
      .catch(() => setQuotes([]));
    axios
      .get(`${API}/admin/quotes`, { withCredentials: true })
      .then((r) => { setAdminQuotes(r.data); setIsAdmin(true); })
      .catch(() => setIsAdmin(false));
  }, [user]);

  const setStatus = async (quoteId, status) => {
    setAdminQuotes((qs) => qs.map((q) => (q.quote_id === quoteId ? { ...q, status } : q)));
    try {
      await axios.patch(`${API}/admin/quotes/${quoteId}/status`, { status }, { withCredentials: true });
    } catch {
      setAdminQuotes((qs) => qs.map((q) => (q.quote_id === quoteId ? { ...q, status: "recue" } : q)));
    }
  };

  const deleteQuote = async (quoteId) => {
    setAdminQuotes((qs) => qs.filter((q) => q.quote_id !== quoteId));
    try {
      await axios.delete(`${API}/admin/quotes/${quoteId}`, { withCredentials: true });
    } catch {
      window.location.reload();
    }
  };

  return (
    <div className="min-h-screen bg-night text-white">
      <div className="noise-overlay" aria-hidden="true" />
      <header className="glass fixed inset-x-0 top-0 z-50 border-b border-line">
        <div className="mx-auto flex h-[72px] w-full max-w-7xl items-center justify-between px-5 lg:px-8">
          <Link data-testid="profile-logo-link" to="/"><Logo /></Link>
          <Link
            data-testid="profile-back-home-btn"
            to="/"
            className="flex items-center gap-2 rounded-full border border-line px-4 py-2.5 text-[13px] font-bold text-white transition-colors duration-200 hover:border-neon hover:text-neon"
          >
            <ArrowLeft size={15} className="text-neon" />
            Accueil
          </Link>
        </div>
      </header>

      <main className="relative mx-auto w-full max-w-4xl px-5 pb-24 pt-32 lg:px-8">
        {checking ? (
          <div className="flex justify-center py-24">
            <Loader2 size={40} className="animate-spin text-neon" />
          </div>
        ) : !user ? (
          <LoginCard login={login} />
        ) : (
          <>
            <div className="flex flex-col items-start gap-6 rounded-3xl border border-line bg-ink p-8 sm:flex-row sm:items-center lg:p-10">
              {user.picture ? (
                <img
                  src={user.picture}
                  alt=""
                  className="h-20 w-20 rounded-2xl border border-neon/30 object-cover"
                  referrerPolicy="no-referrer"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : (
                <span className="flex h-20 w-20 items-center justify-center rounded-2xl border border-neon/30 bg-neon/10 font-display text-3xl font-extrabold text-neon">
                  {user.name?.charAt(0) || "?"}
                </span>
              )}
              <div className="min-w-0">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neon label-mono">Espace client</p>
                <h1 data-testid="profile-user-name" className="mt-1 truncate font-display text-4xl font-extrabold uppercase tracking-tight">{user.name}</h1>
                <p data-testid="profile-user-email" className="mt-1 flex items-center gap-2 truncate text-sm text-slate-400">
                  <Mail size={14} className="shrink-0 text-neon" /> {user.email}
                </p>
                <p className="mt-1 flex items-center gap-2 text-sm text-slate-400">
                  <Phone size={14} className="shrink-0 text-neon" /> {CONTACT.phoneDisplay}
                </p>
              </div>
              <button
                data-testid="profile-logout-btn"
                onClick={logout}
                className="flex items-center gap-2 self-start rounded-full border border-line px-5 py-2.5 text-xs font-extrabold uppercase tracking-wider text-white transition-colors duration-200 hover:border-red-400 hover:text-red-400 sm:ml-auto"
              >
                <LogOut size={14} />
                Déconnexion
              </button>
            </div>

            <h2 className="mt-12 font-display text-2xl font-extrabold uppercase tracking-tight sm:text-3xl">
              Mes demandes de devis
            </h2>

            {quotes === null ? (
              <div className="flex justify-center py-14">
                <Loader2 size={32} className="animate-spin text-neon" />
              </div>
            ) : quotes.length === 0 ? (
              <div className="mt-6 rounded-2xl border border-dashed border-line bg-ink p-10 text-center">
                <p className="text-sm text-slate-400">
                  Aucune demande pour l'instant. Décrivez votre panne depuis la page d'accueil et retrouvez-la ici.
                </p>
                <Link
                  data-testid="profile-no-quote-cta"
                  to="/"
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-neon px-6 py-3 text-xs font-extrabold uppercase tracking-wider text-night chip-glow"
                >
                  <Wrench size={14} />
                  Demander un devis
                </Link>
              </div>
            ) : (
              <ul className="mt-6 grid gap-3">
                {quotes.map((q) => {
                  const meta = STATUS_META[q.status] || STATUS_META.recue;
                  return (
                    <li key={q.quote_id} data-testid={`quote-card-${q.quote_id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-line bg-ink p-5">
                      <div className="flex items-center gap-4">
                        <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-neon/25 bg-neon/10 text-neon">
                          <FileText size={19} />
                        </span>
                        <div>
                          <p className="text-sm font-bold text-white">
                            {q.device_type} {q.device_model ? `· ${q.device_model}` : ""}
                          </p>
                          <p className="text-xs text-slate-500">
                            {q.issue} · {fmtDate(q.created_at)}
                          </p>
                        </div>
                      </div>
                      <span data-testid={`quote-status-${q.quote_id}`} className={`rounded-full border px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] ${meta.cls}`}>
                        {meta.label}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}

            {isAdmin && (
              <section data-testid="admin-panel" className="mt-14 rounded-3xl border border-neon/25 bg-gradient-to-b from-neon/5 via-ink to-ink p-7 lg:p-9">
                <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-neon label-mono">
                  <ShieldCheck size={14} />
                  Administration
                </p>
                <h2 className="mt-3 font-display text-2xl font-extrabold uppercase tracking-tight sm:text-3xl">
                  Toutes les demandes reçues
                </h2>
                <p className="mt-2 text-sm text-slate-400">
                  Changez le statut d'une demande : le client voit la mise à jour dans son espace en temps réel.
                </p>
                {adminQuotes === null ? (
                  <div className="flex justify-center py-10">
                    <Loader2 size={28} className="animate-spin text-neon" />
                  </div>
                ) : (
                  <ul className="mt-6 grid gap-3">
                    {adminQuotes.map((q) => (
                      <li key={q.quote_id} data-testid={`admin-quote-${q.quote_id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-line bg-night p-5">
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-white">
                            {q.name} · {q.device_type} {q.device_model ? `· ${q.device_model}` : ""}
                          </p>
                          <p className="text-xs text-slate-500">
                            {q.issue} · {fmtDate(q.created_at)}
                          </p>
                          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                            <a href={`tel:${q.phone}`} className="font-semibold text-neon hover:underline">{q.phone}</a>
                            <a href={`mailto:${q.email}`} className="text-slate-400 hover:text-neon">{q.email}</a>
                          </p>
                          {q.message && <p className="mt-2 max-w-lg text-xs leading-relaxed text-slate-400">« {q.message} »</p>}
                        </div>
                        <div className="flex items-center gap-2">
                          <select
                            data-testid={`admin-status-select-${q.quote_id}`}
                            value={q.status || "recue"}
                            onChange={(e) => setStatus(q.quote_id, e.target.value)}
                            className="field-input w-auto min-w-[130px] self-start"
                            aria-label={`Statut de la demande de ${q.name}`}
                          >
                            {STATUS_OPTIONS.map(([v, label]) => (
                              <option key={v} value={v}>{label}</option>
                            ))}
                          </select>
                          {confirmDelete === q.quote_id ? (
                            <button
                              data-testid={`admin-delete-confirm-${q.quote_id}`}
                              onClick={() => deleteQuote(q.quote_id)}
                              className="flex items-center gap-1.5 self-start rounded-full border border-red-400/50 bg-red-400/10 px-4 py-2.5 text-[11px] font-extrabold uppercase tracking-wider text-red-400"
                            >
                              <Trash2 size={13} />
                              Confirmer ?
                            </button>
                          ) : (
                            <button
                              data-testid={`admin-delete-${q.quote_id}`}
                              onClick={() => { setConfirmDelete(q.quote_id); setTimeout(() => setConfirmDelete((c) => (c === q.quote_id ? null : c)), 4000); }}
                              className="flex items-center gap-1.5 self-start rounded-full border border-line px-4 py-2.5 text-[11px] font-bold text-slate-400 transition-colors duration-200 hover:border-red-400/50 hover:text-red-400"
                              aria-label={`Supprimer la demande de ${q.name}`}
                            >
                              <Trash2 size={13} />
                              Supprimer
                            </button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}

                {stats !== false && (
                  <div data-testid="analytics-card" className="mt-10 rounded-2xl border border-line bg-night p-6">
                    <p className="label-mono">Audience du site · 30 derniers jours</p>
                    <div className="mt-4 flex flex-wrap items-end gap-8">
                      <div>
                        <p data-testid="analytics-views" className="font-display text-4xl font-extrabold">{stats ? stats.views : "…"}</p>
                        <p className="text-xs text-slate-500">visites</p>
                      </div>
                      <div>
                        <p data-testid="analytics-visitors" className="font-display text-4xl font-extrabold">{stats ? stats.visitors : "…"}</p>
                        <p className="text-xs text-slate-500">visiteurs uniques estimés</p>
                      </div>
                      <p className="ml-auto text-right text-[11px] leading-relaxed text-slate-500">
                        Mesure sans cookie, sans traceur tiers, aucune IP stockée — conforme RGPD.<br />
                        Mobile {stats ? Math.round((stats.devices?.mobile || 0) / Math.max(stats.views, 1) * 100) : "—"} % · Ordinateur {stats ? 100 - Math.round((stats.devices?.mobile || 0) / Math.max(stats.views, 1) * 100) : "—"} %
                      </p>
                    </div>
                    {stats?.daily?.length > 0 && (
                      <div className="mt-6 flex h-14 items-end gap-1" aria-hidden="true">
                        {stats.daily.map((d) => (
                          <div
                            key={d.d}
                            title={`${d.d} : ${d.views} visite${d.views > 1 ? "s" : ""}`}
                            className="flex-1 rounded-t bg-neon/70"
                            style={{ height: `${Math.max(8, Math.round(d.views / Math.max(...stats.daily.map((x) => x.views)) * 100))}%` }}
                          />
                        ))}
                      </div>
                    )}
                    <div className="mt-6 grid gap-6 sm:grid-cols-2">
                      <div>
                        <p className="label-mono">D'où viennent vos visiteurs</p>
                        <ul className="mt-3 grid gap-1.5 text-sm">
                          {(stats?.sources || []).map((s) => (
                            <li key={s.source} className="flex items-center justify-between gap-3">
                              <span className="text-slate-400">{s.source}</span>
                              <span className="font-bold text-white">{s.count}</span>
                            </li>
                          ))}
                          {stats?.sources?.length === 0 && <li className="text-slate-500">Les premières visites apparaîtront ici.</li>}
                        </ul>
                      </div>
                      <div>
                        <p className="label-mono">Pages les plus vues</p>
                        <ul className="mt-3 grid gap-1.5 text-sm">
                          {(stats?.pages || []).map((p) => (
                            <li key={p.path} className="flex items-center justify-between gap-3">
                              <span className="truncate text-slate-400">{p.path}</span>
                              <span className="font-bold text-white">{p.count}</span>
                            </li>
                          ))}
                          {stats?.pages?.length === 0 && <li className="text-slate-500">Les pages vues apparaîtront ici.</li>}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}